import { List, Map } from 'immutable'
import { createSelector } from 'reselect'

import { ImmutableTypedMap } from '@lyrahealth-inc/shared-app-logic/src/features/common/types/Immutable'
import { Appointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import { ConversationObject } from '@lyrahealth-inc/shared-app-logic/src/models/conversation/Conversation'
import { Episode, EpisodeStates } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { CONVERSATION_STATE, CONVERSATION_TYPE } from '@lyrahealth-inc/shared-app-logic/src/models/messages/Messages'
import { Provider, ProviderInfo } from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'

import { getOnboardProviderProfileData } from '../../features/onboard/data/onboardSelectors'
import { getId } from '../user/userSelectors'

const getImmutableLyraTherapyState = (state: Map<string, any>) => state?.get('lyraTherapy')
const getLyraTherapyState = createSelector(getImmutableLyraTherapyState, (immutableLyraTherapyState) => {
  return immutableLyraTherapyState?.toJS()
})
export const getLyraTherapyEpisodes = createSelector(
  getLyraTherapyState,
  (lyraTherapyState) => lyraTherapyState.episodes,
)

export const getLyraTherapy = (state: Map<string, any>) => state?.get('lyraTherapy')

export const getEpisodes = createSelector(getLyraTherapy, (lyraTherapy) => lyraTherapy?.get('episodes', Map()))

export const getInProgressEpisodes = createSelector(getEpisodes, (episodes) =>
  (episodes ?? Map()).filter(
    (episode: ImmutableTypedMap<Episode>) => episode.get('state') === EpisodeStates.IN_PROGRESS,
  ),
)

export const getOutcomes = createSelector(getLyraTherapy, (lyraTherapy) => lyraTherapy?.get('outcomes', Map()))

export const getOutcomesData = createSelector(getOutcomes, (outcomes) => outcomes?.get('data', List()))

export const getSessions = createSelector(getLyraTherapy, (lyraTherapy) => lyraTherapy?.get('sessions'))

export const getPrograms = createSelector(getLyraTherapy, (lyraTherapy) => lyraTherapy?.get('programs') ?? List())

export const getMessenger = createSelector(getLyraTherapy, (lyraTherapy) => lyraTherapy?.get('messenger'))

export const getSelectedProviderId = createSelector(getMessenger, (messenger) => messenger?.get('selectedProviderId'))

export const getSelectedConversationId = createSelector(getMessenger, (messenger) =>
  messenger?.get('selectedConversationId'),
)

export const getConversations = createSelector(getMessenger, (messenger) => messenger?.get('conversations') ?? List())

export const getAICoachConversations = createSelector(getConversations, (conversations) =>
  conversations.filter((conversation: List<ConversationObject>) => {
    const type = conversation?.getIn(['conversation_attributes', 'type'])
    return (
      (type === CONVERSATION_TYPE.AI_COACH || type === CONVERSATION_TYPE.MANAGER_COACHING_AI) &&
      [CONVERSATION_STATE.ACTIVE, CONVERSATION_STATE.INACTIVE].includes(conversation?.getIn(['conversation_state']))
    )
  }),
)

export const getMessengerDrafts = createSelector(getMessenger, (messenger) => messenger?.get('drafts') ?? List())

export const getShowMessengerPopover = createSelector(getMessenger, (messenger) => messenger?.get('showPopover'))

export const getAwaitingAiResponse = createSelector(
  getMessenger,
  (messenger) => messenger?.get('awaitingAiResponse') ?? false,
)

const getImmutableHighAlertTimeoutDeadlines = createSelector(getMessenger, (messenger) =>
  messenger?.get('highAlertTimeoutDeadlines'),
)

export const getHighAlertTimeoutDeadlines = createSelector(
  getImmutableHighAlertTimeoutDeadlines,
  (highAlertTimeoutDeadlines): Record<string, number> => highAlertTimeoutDeadlines?.toJS() ?? {},
)

export const getMessages = createSelector(getMessenger, (messenger) => messenger?.get('messages', Map()))

export const getConversationsClient = createSelector(getMessenger, (messenger) => messenger?.get('conversationsClient'))

export const getInLiveMsgSession = createSelector(getMessenger, (messenger) => messenger?.get('inLiveMsgSession'))

export const getActiveLiveMsgAppt = createSelector(getMessenger, (messenger) => messenger?.get('activeLiveMsgAppt'))

export const getLiveMsgProviderId = createSelector(getActiveLiveMsgAppt, (activeLiveMsgAppt) =>
  activeLiveMsgAppt?.getIn(['provider', 'lyra_id'], ''),
)

export const getLiveMsgProviderIdToOpen = createSelector(getMessenger, (messenger) =>
  messenger?.get('liveMsgProviderIdToOpen'),
)

export const getShownLiveMsgModal = createSelector(getMessenger, (messenger) => messenger?.get('shownModal'))

export const getAppointment = createSelector(getSessions, (sessions) => sessions?.get('appointment'))

export const getAppointmentReschedule = createSelector(getSessions, (sessions) =>
  sessions?.get('appointmentReschedule'),
)

export const getAppointmentReschedulePatientLyraId = createSelector(getSessions, (sessions) =>
  sessions?.get('appointmentReschedulePatientLyraId'),
)

const getSessionEpisodes = createSelector(getSessions, (sessions) => sessions?.get('episodes'))

export const getCurrentEpisodes = createSelector(getSessionEpisodes, (sessionEpisodes) =>
  sessionEpisodes?.get('current'),
)

/**
 * The logged-in user's own in-progress episodes — those where they are the primary patient.
 *
 * `/lt/v1/patient/{lyraId}/episodes/current` returns every episode the lyraId *participates* in, not
 * just their own. A DBT Teens parent is stored on their teen's episode as a SECONDARY_PATIENT, so the
 * raw response includes the teen's episode. Booking-conflict checks must ignore those: a teen's
 * episode is not the parent's care, and it must not block the parent from booking for themselves —
 * including with the teen's own provider. (ACCOUNT-4311)
 */
export const getOwnCurrentEpisodes = createSelector([getCurrentEpisodes, getId], (currentEpisodes, userLyraId) =>
  // Before the user loads there is nothing to compare against, so leave the episodes untouched rather
  // than filtering every one of them away and silently dropping the conflict check.
  userLyraId
    ? currentEpisodes?.filter((episode: ImmutableTypedMap<Episode>) => episode?.get('patient_id') === userLyraId)
    : currentEpisodes,
)

export const getProviders = createSelector(getLyraTherapy, (lyraTherapy) => lyraTherapy?.get('providers'))

export const getSelectedProvider = createSelector([getProviders, getSelectedProviderId], (providers, id) => {
  return providers?.find((provider: Map<string, any>) => provider.getIn(['provider', 'lyra_id']) === id) ?? Map()
})

export const getSelectedConversation = createSelector(
  [getConversations, getSelectedProvider],
  (conversations: ImmutableTypedMap<ConversationObject>[], provider) => {
    return conversations?.find((conversation) => conversation?.get('provider_lyra_id') === provider.get('lyra_id'))
  },
)

const getCurrentProvider = createSelector(
  [getProviders, getOnboardProviderProfileData],
  (providers: ImmutableTypedMap<Provider>[], onboardProviderProfileData: ImmutableTypedMap<ProviderInfo>) => {
    return providers?.find(
      (provider) =>
        (provider?.get('provider') as ImmutableTypedMap<ProviderInfo>)?.get('lyra_id') ===
        onboardProviderProfileData?.get('lyra_id'),
    )
  },
)

// @ts-expect-error TS(2769): No overload matches this call.
export const getCurrentProviderAppointments = createSelector(
  getCurrentProvider,
  (provider: ImmutableTypedMap<Provider>) => provider?.get('appointments'),
)

export const getCurrentProviderActiveEpisodeAppointments = createSelector(
  [getCurrentProviderAppointments, getEpisodes],
  (appointments, episodes) => {
    const typedAppts: List<ImmutableTypedMap<Appointment>> = appointments as unknown as List<
      ImmutableTypedMap<Appointment>
    >
    const currentEpisodeIds = (episodes?.toJS() as Episode[])
      ?.filter((episode) => episode.state === EpisodeStates.IN_PROGRESS)
      .map((episode) => episode.id)
    return typedAppts
      ?.toJS()
      ?.filter(
        (appointment: Appointment) => appointment.episodeId && currentEpisodeIds?.includes(appointment.episodeId),
      )
  },
)

const getCurrentAppointmentProvider = createSelector(
  [getProviders, getAppointment],
  (providers: ImmutableTypedMap<Provider>[], appoinment: ImmutableTypedMap<Appointment>) =>
    providers?.find(
      (provider) =>
        (provider?.get('provider') as ImmutableTypedMap<ProviderInfo>)?.get('lyra_id') ===
        (appoinment?.get('provider') as ImmutableTypedMap<ProviderInfo>)?.get('lyra_id'),
    ),
)

export const getCurrentAppointmentProviderInfo = createSelector(
  getCurrentAppointmentProvider,
  (provider: ImmutableTypedMap<Provider> | undefined) => provider?.get('provider'),
)

export const getCurrentAppointmentProviderActiveEpisodeAppointments = createSelector(
  [getCurrentAppointmentProvider, getEpisodes],
  (provider: ImmutableTypedMap<Provider> | undefined, episodes) => {
    const currentEpisodeIds = (episodes?.toJS() as Episode[])
      ?.filter((episode) => episode.state === EpisodeStates.IN_PROGRESS)
      .map((episode) => episode.id)
    return (
      (provider?.get('appointments') as List<Appointment>)
        ?.toJS()
        ?.filter(
          (appointment: Appointment) => appointment.episodeId && currentEpisodeIds?.includes(appointment.episodeId),
        ) ?? []
    )
  },
)
